import http, { request } from './http';
import type { Result } from './fileApi';

/** 与后端 UploadScenario / UploadLimitsVO 对齐 */
export type UploadLimitScenario = 'project' | 'knowledge' | 'issue' | 'chat' | 'cad' | 'wbs';

export interface UploadLimits {
  projectMaxBytes: number;
  knowledgeMaxBytes: number;
  issueMaxBytes: number;
  chatMaxBytes: number;
  cadMaxBytes: number;
  wbsMaxBytes: number;
  wbsCadMaxBytes: number;
}

const DEFAULT_UPLOAD_LIMITS: UploadLimits = {
  projectMaxBytes: 400 * 1024 * 1024,
  knowledgeMaxBytes: 100 * 1024 * 1024,
  issueMaxBytes: 400 * 1024 * 1024,
  chatMaxBytes: 10 * 1024 * 1024,
  cadMaxBytes: 100 * 1024 * 1024,
  wbsMaxBytes: 400 * 1024 * 1024,
  wbsCadMaxBytes: 400 * 1024 * 1024,
};

let cached: UploadLimits | null = null;
let inflight: Promise<UploadLimits> | null = null;

function asPositiveBytes(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.floor(v);
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return fallback;
}

function normalizeLimits(raw: unknown): UploadLimits {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    projectMaxBytes: asPositiveBytes(o.projectMaxBytes, DEFAULT_UPLOAD_LIMITS.projectMaxBytes),
    knowledgeMaxBytes: asPositiveBytes(o.knowledgeMaxBytes, DEFAULT_UPLOAD_LIMITS.knowledgeMaxBytes),
    issueMaxBytes: asPositiveBytes(o.issueMaxBytes, DEFAULT_UPLOAD_LIMITS.issueMaxBytes),
    chatMaxBytes: asPositiveBytes(o.chatMaxBytes, DEFAULT_UPLOAD_LIMITS.chatMaxBytes),
    cadMaxBytes: asPositiveBytes(o.cadMaxBytes, DEFAULT_UPLOAD_LIMITS.cadMaxBytes),
    wbsMaxBytes: asPositiveBytes(o.wbsMaxBytes, DEFAULT_UPLOAD_LIMITS.wbsMaxBytes),
    wbsCadMaxBytes: asPositiveBytes(o.wbsCadMaxBytes, DEFAULT_UPLOAD_LIMITS.wbsCadMaxBytes),
  };
}

/** 同步读取缓存（未拉取前返回默认值，与后端 yaml 默认一致） */
export function getUploadLimits(): UploadLimits {
  return cached ?? DEFAULT_UPLOAD_LIMITS;
}

export function maxBytesForScenario(scenario: UploadLimitScenario, limits = getUploadLimits()): number {
  switch (scenario) {
    case 'knowledge':
      return limits.knowledgeMaxBytes;
    case 'issue':
      return limits.issueMaxBytes;
    case 'chat':
      return limits.chatMaxBytes;
    case 'cad':
      return limits.cadMaxBytes;
    case 'wbs':
      return limits.wbsMaxBytes;
    case 'project':
    default:
      return limits.projectMaxBytes;
  }
}

export function formatUploadLimitLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0MB';
  const gb = 1024 * 1024 * 1024;
  if (bytes >= gb) {
    const v = bytes / gb;
    return `${Math.abs(v - Math.round(v)) < 0.05 ? Math.round(v) : v.toFixed(1)}GB`;
  }
  return `${Math.max(1, Math.round(bytes / (1024 * 1024)))}MB`;
}

/** 拉取并缓存分场景上传上限（可并行调用，只打一次请求） */
export async function ensureUploadLimits(force = false): Promise<UploadLimits> {
  if (!force && cached) return cached;
  if (!force && inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await request<Result<UploadLimits>>(http.get('/app/upload-limits'));
      const r = res as unknown as Result<UploadLimits>;
      if (r?.success && r.data) {
        cached = normalizeLimits(r.data);
      } else {
        console.warn('[upload-limits] unexpected response, keep previous or defaults', r);
        if (!cached) cached = { ...DEFAULT_UPLOAD_LIMITS };
      }
    } catch (e) {
      console.warn('[upload-limits] fetch failed, using defaults until next success', e);
      if (!cached) cached = { ...DEFAULT_UPLOAD_LIMITS };
    } finally {
      inflight = null;
    }
    return cached!;
  })();
  return inflight;
}
